/**
 * "Include costs" selection for the Marketing report, URL-driven via `?costs=`.
 *
 * The cost side of a month is ad spend plus whichever cost heads the operator
 * has ticked. Ticking is the operator's call, never automatic: an unticked head
 * is out of Total Spend, out of every ratio, and out of the completeness gate.
 *
 * Three states, so the URL can say each one honestly:
 *   - param absent        -> every active head (what a fresh visit gets)
 *   - `costs=a,b`         -> exactly those heads
 *   - `costs=none`        -> no heads at all, ad spend only
 *
 * `none` is a sentinel rather than an empty value because an empty param reads
 * as "unset" everywhere else in the stack and would silently mean "all".
 */

const COSTS_NONE = 'none';
const CODE = /^[a-z0-9_]+$/;

/** Read `?costs=`. `null` = not set (every active head); `[]` = none. */
export const parseCostsParam = (raw: string | null): string[] | null => {
  if (raw === null) return null;
  const parts = [
    ...new Set(
      raw
        .split(',')
        .map((part) => part.trim())
        .filter((part) => CODE.test(part)),
    ),
  ].sort();
  if (parts.includes(COSTS_NONE)) return [];
  return parts.length > 0 ? parts : null;
};

/** Serialise for the URL / query key - sorted so keys stay stable. */
export const serialiseCosts = (codes: string[]): string =>
  codes.length > 0 ? [...codes].sort().join(',') : COSTS_NONE;

/**
 * The codes a selection resolves to against the live head list. `null` means
 * every active head; an explicit list is clipped to heads that still exist so
 * a stale link with a deleted head doesn't carry a ghost.
 */
export const resolveSelectedCodes = (
  selection: string[] | null,
  activeCodes: ReadonlyArray<string>,
): string[] => {
  if (selection === null) return [...activeCodes];
  const live = new Set(activeCodes);
  return selection.filter((code) => live.has(code));
};

/** Trigger label: "All costs", "2 of 4 costs", "No costs". */
export const describeCosts = (selected: ReadonlyArray<string>, total: number): string => {
  if (total === 0) return 'No cost heads';
  if (selected.length === 0) return 'Ad spend only';
  if (selected.length === total) return 'All costs';
  return `${selected.length} of ${total} costs`;
};
